import { Form, Formik } from "formik";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { OverlayPanel } from "primereact/overlaypanel";
import { useContext, useRef, useState } from "react";
import styled from "styled-components";
import * as Yup from "yup";
import avatar from "../../../Assets/images/avatar.svg";
import CalendarComponent from "../../../Components/Calendar";
import DropdownComponent from "../../../Components/Dropdown";
import ErrorSummary from "../../../Components/ErrorSummary";
import FieldError from "../../../Components/FieldError";
import Icon from "../../../Components/Icon";
import MaskInput from "../../../Components/InputMask";
import InputAddress from "../../../Components/InputsAddress";
import Loading from "../../../Components/Loading";
import StepsNavigator, { StepItem } from "../../../Components/StepsNavigator";
import TextInput from "../../../Components/TextInput";
import BeneficiariesEditProvider, {
  BeneficiariesEditContext,
} from "../../../Context/Beneficiaries/BeneficiaresEdit/context";
import { BeneficiariesEditType } from "../../../Context/Beneficiaries/BeneficiaresEdit/type";
import {
  color_race,
  formatarData,
  getErrorsAsArray,
  getStatus,
  kinship,
  StatusRegistrationEnum,
  StatusTermEnum,
  typesex,

} from "../../../Controller/controllerGlobal";
import { validaCPF } from "../../../Controller/controllerValidCPF";
import styles from "../../../Styles";
import color from "../../../Styles/colors";
import { Container, Padding, Row } from "../../../Styles/styles";

import { Popover } from "react-tiny-popover";
import ModalAddTerm from "./ModalAddTerm";
import ModalCreateRegisterClassroom from "./ModalCreateRegisterClassroom";
import CheckboxComponent from "../../../Components/Checkbox";
import RadioButtonComponent from "../../../Components/RadioButton";
import {
  isUnder18ByBirthDate,
  shouldRequireBeneficiaryPhone,
} from "../../../Utils/beneficiaryRules";
import { usePermissions } from "../../../hooks/usePermissions";
import { normalizeName } from "../../../Utils/normalizeName";

const schema = Yup.object().shape({
  name: Yup.string().required("Nome é obrigatório"),
  color_race: Yup.object().required("Raça/cor é obrigatório"),
  deficiency: Yup.object().required("Deficiência é obrigatória"),
  cpf: Yup.string()
    .test("cpf-valid", "CPF inválido", (value) => {
      if (value && value.trim() !== "") {
        return validaCPF(value);
      }
      return true;
    })
    .required("CPF é obrigatório"),
  responsable_cpf: Yup.string()
    .nullable()
    .transform((value) => (value === "" || value === null ? undefined : value))
    .test("cpf-valid", "CPF inválido", (value) => {
      if (value && value.trim() !== "") {
        return validaCPF(value);
      }
      return true;
    })
    .when("birthday", {
      is: (birthday: string) => isUnder18ByBirthDate(birthday),
      then: (s) => s.required("CPF do responsável é obrigatório para menores de 18 anos"),
      otherwise: (s) => s.optional(),
    }),
  responsable_name: Yup.string()
    .nullable()
    .transform((value) => (value === "" || value === null ? undefined : value))
    .when("birthday", {
      is: (birthday: string) => isUnder18ByBirthDate(birthday),
      then: (s) => s.required("Nome do responsável é obrigatório para menores de 18 anos"),
      otherwise: (s) => s.optional(),
    }),
  responsable_telephone: Yup.string()
    .nullable()
    .transform((value) => (value === "" || value === null ? undefined : value))
    .when("birthday", {
      is: (birthday: string) => isUnder18ByBirthDate(birthday),
      then: (s) => s.required("Telefone do responsável é obrigatório para menores de 18 anos"),
      otherwise: (s) => s.optional(),
    }),
  responsable_email: Yup.string()
    .nullable()
    .transform((value) => (value === "" || value === null ? undefined : value))
    .email("E-mail do responsável inválido"),
  kinship: Yup.string()
    .nullable()
    .transform((value) => (value === "" || value === null || value === "NAO_DEFINIDO" ? undefined : value))
    .when("birthday", {
      is: (birthday: string) => isUnder18ByBirthDate(birthday),
      then: (s) => s.required("Parentesco é obrigatório para menores de 18 anos"),
      otherwise: (s) => s.optional(),
    }),
  telephone: Yup.string().when("birthday", {
    is: (birthday: string) => shouldRequireBeneficiaryPhone(birthday),
    then: (s) => s.required("Telefone para contato é obrigatório"),
    otherwise: (s) => s.optional(),
  }),
  is_legal_responsible: Yup.boolean().when("birthday", {
    is: (birthday: string) => isUnder18ByBirthDate(birthday),
    then: (s) =>
      s
        .oneOf([true], "É necessário confirmar que é o responsável legal do menor")
        .required("É necessário confirmar que é o responsável legal do menor"),
    otherwise: (s) => s.optional(),
  }),
  birthday: Yup.string().nullable().required("Data de nascimento é obrigatória"),
  zone: Yup.string().nullable().required("Zona é obrigatório"),
  neighborhood: Yup.string().nullable().required("Bairro/Povoado é obrigatória"),
  address: Yup.string().nullable().required("Endereço é obrigatória"),
  state: Yup.string().nullable().required("Estado é obrigatório"),
  city: Yup.string().nullable().required("Cidade é obrigatório"),
  sex: Yup.object().nullable().required("Sexo é obrigatória"),
});

const STEPS_EDIT: StepItem[] = [
  { key: "dados_basicos", label: "Dados Básicos", description: "Informações pessoais do beneficiário." },
  { key: "responsavel", label: "Dados do Responsável", description: "Obrigatório para menores de 18 anos." },
  { key: "endereco", label: "Endereço", description: "Localização do beneficiário." },
  { key: "termos", label: "Termos & Matrículas", description: "Histórico de termos e matrículas." },
];

const STEPS_EDIT_LIMITED: StepItem[] = [
  { key: "dados_basicos", label: "Dados", description: "Nome e CPF do beneficiário." },
  { key: "termos", label: "Termos & Matrículas", description: "Histórico de termos e matrículas." },
];

export const maskCpf = (cpf: string) => {
  const digits = (cpf || '').replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.***.***-${digits.slice(9)}`;
};

const BeneficiariesEdit = () => {
  return (
    <BeneficiariesEditProvider>
      <BeneficiariesEditPage />
    </BeneficiariesEditProvider>
  );
};

export const Avatar = styled.div`
  border: 1px solid ${styles.colors.colorBorderCard};
  height: 128px;
  width: 128px;
  border-radius: 50%;

  img {
    border-radius: 50%; /* This will make the image circular */
    height: 100%;
    width: 100%;
  }
`;

const termStatusInfo = [
  {
    label: "Termo em análise",
    color: "#f59e0b",
    icon: "pi pi-clock",
    description:
      "Status inicial atribuído automaticamente ao anexar o documento. O termo aguarda revisão pela equipe de M&A.",
  },
  {
    label: "Termo ativo",
    color: "#10b981",
    icon: "pi pi-check-circle",
    description:
      "Documento validado e aceito pela equipe de M&A. O beneficiário está com o termo regularizado e em plena vigência.",
  },
  {
    label: "Termo inativo",
    color: "#6b7280",
    icon: "pi pi-minus-circle",
    description:
      "O termo perdeu a validade. Pode ocorrer automaticamente quando o prazo expira ou o beneficiário fica inativo no THP (Termo de Adesão), ou ser definido manualmente pela equipe de M&A.",
  },
  {
    label: "Termo inválido",
    color: "#ef4444",
    icon: "pi pi-times-circle",
    description:
      "Documento recusado pela equipe de M&A por não atender aos requisitos exigidos. É necessário anexar um novo documento para regularizar a situação.",
  },
];

const StatusTermHeader = () => {
  const op = useRef<OverlayPanel>(null);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <span>Status do termo</span>
      <button
        type="button"
        onClick={(e) => op.current?.toggle(e)}
        style={{
          background: "#6b7280",
          border: "none",
          borderRadius: "50%",
          width: "18px",
          height: "18px",
          color: "white",
          fontSize: "11px",
          fontWeight: "bold",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
        title="Ver descrição dos status"
      >
        ?
      </button>
      <OverlayPanel ref={op} style={{ maxWidth: "360px" }}>
        <p style={{ fontWeight: 600, marginBottom: "12px", fontSize: "14px", color: "#1e293b" }}>
          Legenda dos status de termo
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {termStatusInfo.map((item) => (
            <div key={item.label} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
              <i
                className={item.icon}
                style={{ color: item.color, fontSize: "16px", marginTop: "2px", flexShrink: 0 }}
              />
              <div>
                <p style={{ fontWeight: 600, fontSize: "13px", color: item.color, margin: 0 }}>
                  {item.label}
                </p>
                <p style={{ fontSize: "12px", color: "#475569", margin: "2px 0 0" }}>
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </OverlayPanel>
    </div>
  );
};

const BeneficiariesEditPage = () => {
  const props = useContext(BeneficiariesEditContext) as BeneficiariesEditType;
  const { can } = usePermissions();
  const canEdit          = can("beneficiary.edit");
  const canAddEnrollment = can("registration.create");
  const canViewFull      = can("beneficiary.viewFull");
  const [visible, setVisible] = useState<any>();
  const [visibleTerm, setVisibleTerm] = useState<any>();
  const [visibleDeleteTerm, setVisibleDeleteTerm] = useState<any>();
  const [visiblePdfViewer, setVisiblePdfViewer] = useState(false);
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string>("");
  const [pdfViewerLoading, setPdfViewerLoading] = useState(false);
  const [pdfViewerError, setPdfViewerError] = useState<string>("");
  const objectUrlRef = useRef<string>("");
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  // Maps limited-view step index (0 or 1) to full step content index (0 or 3)
  const actualStep = canViewFull ? currentStep : ([0, 3][currentStep] ?? 0);

  const closePdfViewer = () => {
    setVisiblePdfViewer(false);
    setPdfViewerUrl("");
    setPdfViewerLoading(false);
    setPdfViewerError("");

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = "";
    }
  };

  const openPdfViewer = async (url: string) => {
    if (!url) {
      return;
    }

    setVisiblePdfViewer(true);
    setPdfViewerLoading(true);
    setPdfViewerError("");

    try {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = "";
      }

      const response = await fetch(url, {
        headers: {
          Accept: "application/pdf",
        },
      });
      if (!response.ok) {
        throw new Error("Falha ao carregar o arquivo.");
      }

      const buffer = await response.arrayBuffer();
      const pdfBlob = new Blob([buffer], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(pdfBlob);
      objectUrlRef.current = blobUrl;
      setPdfViewerUrl(blobUrl);
    } catch (error) {
      setPdfViewerUrl(url);
      setPdfViewerError(
        "Não foi possível abrir a pré-visualização agora. Você ainda pode abrir em nova guia."
      );
    } finally {
      setPdfViewerLoading(false);
    }
  };

  const [visibleDelete, setVisibleDelete] = useState<any>();

  if (props.isLoading) return <Loading />;

  const renderHeader = () => {
    return (
      <div
        className="flex justify-content-between"
        style={{ background: color.colorCard }}
      >
        {(canEdit || canAddEnrollment) && (
          <Button
            label="Nova matricula"
            icon="pi pi-plus"
            onClick={() => setVisible(true)}
          />
        )}
      </div>
    );
  };

  const renderHeaderTerm = () => {
    return (
      <div
        className="flex justify-content-between"
        style={{ background: color.colorCard }}
      >
        {(canEdit || canAddEnrollment) && (
          <Button
            label={"Novo termo"}
            icon="pi pi-plus"
            type="button"
            onClick={() => setVisibleTerm(true)}
          />
        )}
      </div>
    );
  };




  const StatusBody = (rowData: any) => {
    return <div>{getStatus(rowData?.status)?.name}</div>;
  };

  const ActionBeneficiariesBody = (rowData: any) => {
    return (
      <Row id="center">
        {canEdit && (
          <Button
            severity="danger"
            rounded
            icon={"pi pi-trash"}
            onClick={() => {
              setVisibleDelete(rowData);
            }}
          />
        )}
      </Row>
    );
  };



  return (
    <Container>
      <h1>Editar Beneficiario</h1>
      <Padding padding="16px" />
      {props.registrations ? (
        <Formik
          initialValues={props.initialValue}
          validationSchema={schema}
          onSubmit={(values) => {
            props.handleUpdateRegistration(
              { ...values, name: values.name?.trim() },
              props.registrations?.id!
            );
          }}
        >
          {({ values, handleChange, errors, touched, setFieldValue, validateForm }) => {
            const errorArray = submitted ? getErrorsAsArray(errors) : [];
            const fieldError = (field: string) =>
              submitted ? (errors as any)[field] : undefined;

            const handleBirthdayChange = (e: any) => {
              handleChange(e);
              // Re-valida após mudança de birthday para atualizar campos condicionais
              setTimeout(() => validateForm(), 0);
            };
            return (
              <Form>
                <div>
                  <Row id="end">
                    {!canEdit && (
                      <p style={{ color: "#6b7280", fontSize: "13px" }}>
                        Você está no modo de visualização.
                      </p>
                    )}
                    {canEdit && (
                      <Button
                        label="Salvar"
                        type="submit"
                        loading={props.isLoadingUpdate}
                        onClick={() => setSubmitted(true)}
                        icon="pi pi-save"
                      />
                    )}
                  </Row>
                </div>
                <Padding padding="8px" />
                <ErrorSummary errors={errorArray} />
                <Padding padding="8px" />
                <StepsNavigator
                  steps={canViewFull ? STEPS_EDIT : STEPS_EDIT_LIMITED}
                  currentStep={currentStep}
                  onStepChange={setCurrentStep}
                  showActions={false}
                />

                <Padding padding="8px" />
                {actualStep === 0 && (<>
                  {canViewFull && (
                    <>
                      <Avatar>
                        <img
                          alt=""
                          src={
                            props.file
                              ? URL.createObjectURL(props.file![0]) ?? undefined
                              : props.registrations?.avatar_url
                                ? props.registrations?.avatar_url
                                : avatar
                          }
                        />
                      </Avatar>
                      <Padding padding="8px" />
                      <div className="grid">
                        <div className="col-12 md:col-6">
                          <label>Avatar </label>
                          <Padding />
                          <TextInput
                            type="file"
                            placeholder="Avatar"
                            disabled={!canEdit}
                            onChange={(e) => props.setFile(e.target.files)}
                            name="name"
                          />
                        </div>
                      </div>
                      <Padding padding="8px" />
                    </>
                  )}
                  <h3>Dados basicos</h3>
                  <Padding />
                  <div className="grid">
                    <div className="col-12 md:col-6">
                      <label>Id THP</label>
                      <Padding />
                      <TextInput
                        value={props.registrations?.thp_id}
                        placeholder="Id THP"
                        disabled
                        name="thp_id"
                      />
                    </div>
                    <div className="col-12 md:col-6">
                      <label>Status do beneficiário</label>
                      <Padding />
                      <TextInput
                        value={props.registrations?.status ? StatusRegistrationEnum[props.registrations?.status] : props.registrations?.status}
                        name="status"
                        disabled
                      />
                    </div>
                  </div>
                  <div className="grid">
                    <div className="col-12 md:col-6">
                      <label>Nome *</label>
                      <Padding />
                      <TextInput
                        value={values.name}
                        placeholder="Nome"
                        disabled={!canEdit}
                        name="name"
                        onChange={(e) => setFieldValue('name', normalizeName(e.target.value))}
                      />
                      {errors.name && touched.name ? (
                        <FieldError message={fieldError("name")} />
                      ) : null}
                    </div>
                    {canViewFull && (
                      <div className="col-12 md:col-6">
                        <label>Sexo *</label>
                        <Padding />
                        <DropdownComponent
                          value={values.sex}
                          optionsLabel="type"
                          options={typesex}
                          name="sex"
                          disabled={!canEdit}
                          onChange={handleChange}
                        />
                        {errors.sex && touched.sex ? (
                          <FieldError message={fieldError("sex")} />
                        ) : null}
                      </div>
                    )}
                  </div>
                  {canViewFull && (
                    <div className="grid">
                      <div className="col-12 md:col-6">
                        <label>Data de Nascimento *</label>
                        <Padding />
                        <MaskInput
                          value={values.birthday?.toString()}
                          mask="99/99/9999"
                          placeholder="Data de Nascimento"
                          name="birthday"
                          disabled={!canEdit}
                          onChange={handleBirthdayChange}
                        />
                        {errors.birthday && touched.birthday ? (
                          <FieldError message={fieldError("birthday")} />
                        ) : null}
                      </div>
                      <div className="col-12 md:col-6">
                        <label>Cor de raça *</label>
                        <Padding />
                        <DropdownComponent
                          value={values.color_race}
                          options={color_race}
                          name="color_race"
                          disabled={!canEdit}
                          onChange={handleChange}
                        />
                        {errors.color_race && touched.color_race ? (
                          <FieldError message={fieldError("color_race")} />
                        ) : null}
                      </div>
                    </div>
                  )}
                  <div className="grid">
                    <div className="col-12 md:col-6">
                      <label>CPF *</label>
                      <Padding />
                      {canViewFull ? (
                        <MaskInput
                          value={values.cpf}
                          mask="999.999.999-99"
                          placeholder="CPF *"
                          disabled={!canEdit}
                          onChange={handleChange}
                          name="cpf"
                        />
                      ) : (
                        <TextInput
                          value={maskCpf(values.cpf ?? '')}
                          placeholder="CPF"
                          disabled
                          name="cpf"
                        />
                      )}
                      {errors.cpf && touched.cpf ? (
                        <FieldError message={fieldError("cpf")} />
                      ) : null}
                    </div>
                    {canViewFull && (
                      <>
                        <div className="col-12 md:col-6">
                          <label>Data de matricula</label>
                          <Padding />
                          <CalendarComponent
                            value={values.date_registration}
                            name="date_registration"
                            dateFormat="dd/mm/yy"
                            disabled={!canEdit}
                            onChange={handleChange}
                          />
                          {errors.date_registration && touched.date_registration ? (
                            <FieldError message={fieldError("date_registration")} />
                          ) : null}
                        </div>
                        <div className="col-12 md:col-6">
                          <label>
                            Telefone para contato{shouldRequireBeneficiaryPhone(values.birthday?.toString() ?? "") ? " *" : ""}
                          </label>
                          <Padding />
                          <MaskInput
                            value={values.telephone}
                            mask="(99) 9 9999-9999"
                            name="telephone"
                            disabled={!canEdit}
                            onChange={handleChange}
                            placeholder="Telefone para contato"
                          />
                          {errors.telephone && touched.telephone ? (
                            <FieldError message={fieldError("telephone")} />
                          ) : null}
                        </div>
                        <div className="col-12 md:col-6">
                          <label>Deficiente *</label>
                          <Padding />
                          <DropdownComponent
                            value={values.deficiency}
                            placerholder="Deficiente"
                            name="deficiency"
                            disabled={!canEdit}
                            onChange={handleChange}
                            options={[
                              { id: true, name: "Sim" },
                              { id: false, name: "Não" },
                            ]}
                          />
                          {errors.deficiency && touched.deficiency ? (
                            <FieldError message={fieldError("deficiency")} />
                          ) : null}
                        </div>
                        {values.deficiency?.id && (
                          <div className="col-12 md:col-6">
                            <label>Qual deficiência?</label>
                            <Padding />
                            <TextInput
                              value={values.deficiency_description}
                              name="deficiency_description"
                              disabled={!canEdit}
                              onChange={handleChange}
                              placeholder="Qual deficiência ?"
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {canViewFull && (
                    <div className="grid">
                      <div className="col-12 md:col-6">
                        <label>Zona *</label>
                        <Padding />
                        <Row className="gap-2">
                          <RadioButtonComponent
                            value={1}
                            checked={values.zone === 1}
                            onChange={handleChange}
                            name="zone"
                            disabled={!canEdit}
                            label="Rural"
                          />
                          <RadioButtonComponent
                            value={2}
                            checked={values.zone === 2}
                            onChange={handleChange}
                            name="zone"
                            disabled={!canEdit}
                            label="Urbana"
                          />
                        </Row>
                        {errors.zone && touched.zone ? (
                          <FieldError message={fieldError("zone")} />
                        ) : null}
                      </div>
                    </div>
                  )}
                  <Padding padding="8px" />
                </>)}
                {actualStep === 1 && (<>
                  <h3>Dados Responsavel (Se for menor de 18 anos)</h3>
                  <Padding />
                  {isUnder18ByBirthDate(values.birthday?.toString() ?? "") ? (
                    <>
                      <div className="grid">
                        <div className="col-12 md:col-6">
                          <label>Nome *</label>
                          <Padding />
                          <TextInput
                            value={values.responsable_name}
                            name="responsable_name"
                            disabled={!canEdit}
                            onChange={handleChange}
                            placeholder="Nome do Resposável"
                          />
                          {errors.responsable_name && touched.responsable_name ? (
                            <FieldError message={fieldError("responsable_name")} />
                          ) : null}
                        </div>
                        <div className="col-12 md:col-6">
                          <label>CPF Responsável *</label>
                          <Padding />
                          <MaskInput
                            value={values.responsable_cpf}
                            mask="999.999.999-99"
                            name="responsable_cpf"
                            disabled={!canEdit}
                            placeholder="CPF do Responsável"
                            onChange={handleChange}
                          />
                          {errors.responsable_cpf && touched.responsable_cpf ? (
                            <FieldError message={fieldError("responsable_cpf")} />
                          ) : null}
                        </div>
                      </div>{" "}
                      <div className="grid">
                        <div className="col-12 md:col-6">
                          <label>Parentesco *</label>
                          <Padding />
                          <DropdownComponent
                            placerholder="Parantesco"
                            onChange={handleChange}
                            options={kinship}
                            name="kinship"
                            optionsValue="id"
                            optionsLabel="name"
                            disabled={!canEdit}
                            value={values.kinship}
                          />
                          {errors.kinship && touched.kinship ? (
                            <FieldError message={fieldError("kinship")} />
                          ) : null}
                        </div>
                        <div className="col-12 md:col-6">
                          <label>
                            Telefone do Responsável{isUnder18ByBirthDate(values.birthday?.toString() ?? "") ? " *" : ""}
                          </label>
                          <Padding />
                          <MaskInput
                            value={values.responsable_telephone}
                            mask="(99) 9 9999-9999"
                            name="responsable_telephone"
                            disabled={!canEdit}
                            onChange={handleChange}
                            placeholder="Telefone do Responsável"
                          />
                          {errors.responsable_telephone && touched.responsable_telephone ? (
                            <FieldError message={fieldError("responsable_telephone")} />
                          ) : null}
                        </div>
                        <div className="col-12 md:col-6">
                          <label>
                            E-mail do Responsável
                          </label>
                          <Padding />
                          <TextInput
                            value={values.responsable_email}
                            name="responsable_email"
                            disabled={!canEdit}
                            onChange={handleChange}
                            placeholder="E-mail do Responsável"
                          />
                          {errors.responsable_email && touched.responsable_email ? (
                            <FieldError message={fieldError("responsable_email")} />
                          ) : null}
                        </div>
                        {isUnder18ByBirthDate(values.birthday?.toString() ?? "") && (
                          <div className="col-12">
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                              <CheckboxComponent
                                checked={values.is_legal_responsible}
                                disabled={!canEdit}
                                onChange={(e) => setFieldValue("is_legal_responsible", e.checked)}
                              />
                              <label style={{ cursor: "pointer", fontWeight: 500 }}>
                                Confirmo que sou o responsável legal deste menor de idade *
                              </label>
                            </div>
                            {errors.is_legal_responsible && touched.is_legal_responsible ? (
                              <FieldError message={fieldError("is_legal_responsible")} />
                            ) : null}
                          </div>
                        )}
                      </div>{" "}
                    </>
                  ) : (
                    <div style={{ color: "#6b7280", fontSize: "14px" }}>
                      Beneficiário maior de 18 anos: os dados de responsável legal não são necessários.
                    </div>
                  )}
                  <Padding />
                </>)}
                {actualStep === 2 && (<>
                  <h3>Endereço</h3>
                  <Padding padding="8px" />
                  <InputAddress
                    errors={errors}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    showRequiredAsterisk
                    touched={touched}
                    values={values}
                    disabled={!canEdit}
                  />
                </>)}
                {actualStep === 3 && (<>
                  <Padding padding="8px" />
                  <h3>Autorização de imagem</h3>
                  <Padding padding="8px" />
                  <div className="grid">
                    <div className="col-12">
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <CheckboxComponent
                          checked={values.image_sharing_not_authorized}
                          disabled={!canEdit}
                          onChange={(e) =>
                            setFieldValue("image_sharing_not_authorized", e.checked)
                          }
                        />
                        <label style={{ cursor: "pointer", fontWeight: 500 }}>
                          O beneficiário ou responsável legal não autorizou o uso da imagem.
                        </label>
                      </div>
                    </div>
                  </div>
                  <Padding padding="8px" />
                  <h3>Termo</h3>
                  <Padding padding="8px" />
                  <DataTable
                    value={props.registrations?.register_term}
                    tableStyle={{ minWidth: "50rem" }}
                    header={renderHeaderTerm}
                  >
                    <Column
                      body={(row) => {
                        return <>{formatarData(row?.dateTerm!)}</>;
                      }}
                      header="Data de assinatura"
                    ></Column>
                    <Column
                      body={(row) => {
                        return <>{formatarData(row?.dateValid ?? "")}</>;
                      }}
                      header="Data de validade"
                    ></Column>
                    <Column
                      body={(row) => {
                        return (

                          <>
                            {row?.term_type?.label ?? row?.type ?? ""}
                          </>
                        );
                      }}
                      header="Tipo do termo"
                    ></Column>
                    <Column
                      body={(row) => {
                        const statusKey = row?.status ?? "";
                        const info = termStatusInfo.find((i) => {
                          const keyMap: Record<string, string> = {
                            TERM_ANALYSIS: "Termo em análise",
                            ACTIVE_TERM: "Termo ativo",
                            INACTIVE_TERM: "Termo inativo",
                            INVALID_TERM: "Termo inválido",
                          };
                          return i.label === keyMap[statusKey];
                        });
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            {info && (
                              <i className={info.icon} style={{ color: info.color, fontSize: "14px" }} />
                            )}
                            <span>{StatusTermEnum[statusKey] ?? "-"}</span>
                          </div>
                        );
                      }}
                      header={<StatusTermHeader />}
                    ></Column>
                    <Column
                      body={(row) => {
                        return (
                          <>
                            {row?.observation}
                          </>
                        );
                      }}
                      header="Observações"
                    ></Column>
                    <Column
                      align={"center"}
                      body={(e) => (
                        <RenderActionTerm
                          row={e}
                          canEdit={canEdit}
                          setVisibleDeleteTerm={setVisibleDeleteTerm}
                          setVisibleTerm={setVisibleTerm}
                          onOpenPdfViewer={(url: string) => {
                            openPdfViewer(url);
                          }}
                        />
                      )}
                      header="Ações"
                    ></Column>
                  </DataTable>
                  <Padding padding="8px" />
                  <h3>Matriculas</h3>
                  <Padding padding="8px" />
                  <DataTable
                    value={props.registrations?.register_classroom}
                    tableStyle={{ minWidth: "50rem" }}
                    header={renderHeader}
                  >
                    <Column
                      field="classroom.project.name"
                      header="Plano de trabalho"
                    ></Column>
                    <Column field="classroom.name" header="Turma"></Column>
                    <Column body={StatusBody} header="Status da matrícula"></Column>
                    <Column header="Ações" body={ActionBeneficiariesBody}></Column>
                  </DataTable>
                </>)}

                <StepsNavigator
                  steps={canViewFull ? STEPS_EDIT : STEPS_EDIT_LIMITED}
                  currentStep={currentStep}
                  onStepChange={setCurrentStep}
                  onlyActions
                />
              </Form>
            );
          }}
        </Formik>
      ) : null}
      <ModalCreateRegisterClassroom
        onHide={() => setVisible(false)}
        visible={visible}
      />
      <ModalAddTerm
        onHide={() => setVisibleTerm(false)}
        visible={visibleTerm}
        id={props.registrations?.id!}
      />
      <Dialog
        header="Visualizar termo"
        visible={visiblePdfViewer}
        onHide={closePdfViewer}
        style={{ width: window.innerWidth > 800 ? "80vw" : "95vw" }}
      >
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "12px",
            marginBottom: "12px",
            color: "#334155",
          }}
        >
          Confira o termo abaixo. Se preferir, você pode abrir em nova guia.
        </div>
        {pdfViewerLoading ? (
          <p>Carregando visualização do termo...</p>
        ) : null}
        {pdfViewerError ? (
          <p style={{ color: "#b91c1c", marginBottom: "12px" }}>{pdfViewerError}</p>
        ) : null}
        {pdfViewerUrl && !pdfViewerLoading ? (
          <div>
            <object
              data={pdfViewerUrl}
              type="application/pdf"
              style={{ width: "100%", height: "75vh", border: "none" }}
            >
              <iframe
                title="Visualizador de termo"
                src={pdfViewerUrl}
                style={{ width: "100%", height: "75vh", border: "none" }}
              />
            </object>
            <Padding padding="8px" />
            <a href={pdfViewerUrl} target="_blank" rel="noreferrer">
              Abrir em nova guia
            </a>
          </div>
        ) : null}
      </Dialog>
      <ConfirmDialog
        visible={visibleDelete}
        onHide={() => setVisibleDelete(false)}
        message="Tem certeza de que deseja prosseguir?"
        header="Confirmação"
        icon="pi pi-exclamation-triangle"
        accept={() => props.DeleteRegistration(visibleDelete.id)}
        reject={() => setVisibleDelete(false)}
      />

      <ConfirmDialog
        visible={visibleDeleteTerm}
        onHide={() => setVisibleDeleteTerm(false)}
        message="Tem certeza de que deseja prosseguir?"
        header="Confirmação"
        icon="pi pi-exclamation-triangle"
        accept={() => props.DeleteRegisterTerm(visibleDeleteTerm.id)}
        reject={() => setVisibleDeleteTerm(false)}
      />
    </Container>
  );
};

const RenderActionTerm = ({
  row,
  setVisibleTerm,
  setVisibleDeleteTerm,
  onOpenPdfViewer,
  canEdit,
}: {
  row: any,
  setVisibleTerm: any,
  setVisibleDeleteTerm: any,
  onOpenPdfViewer: (url: string) => void,
  canEdit: boolean,
}) => {

  const [isPopoverOpen, setIsPopoverOpen] = useState<any>();

  return (
    <Row id="center" style={{ gap: "8px" }}>
      <Popover
        isOpen={isPopoverOpen}
        positions={["top", "bottom", "left", "right"]} // preferred positions by priority
        onClickOutside={() => setIsPopoverOpen(!isPopoverOpen)}
        content={
          <div
            style={{
              backgroundColor: "white",
              padding: "8px",
              minWidth: "128px",
              boxShadow:
                "rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px",
            }}
          >
            <Row
              onClick={() => {
                onOpenPdfViewer(row?.blob_file?.blob_url);
                setIsPopoverOpen(!isPopoverOpen);
              }}
              id="space-between"
              style={{ cursor: "pointer", padding: "8px", gap: "8px" }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Icon icon="pi pi-eye" size="16px" />
              </div>
              <p>Visualizar</p>
            </Row>
            <Row
              onClick={() => {
                window.open(row.blob_file.blob_url);
                setIsPopoverOpen(!isPopoverOpen);
              }}
              id="space-between"
              style={{ cursor: "pointer", padding: "8px", gap: "8px" }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Icon icon="pi pi-download" size="16px" />
              </div>
              <p>Baixar</p>
            </Row>
            {canEdit && (
              <Row
                onClick={() => {
                  setVisibleTerm(row);
                  setIsPopoverOpen(!isPopoverOpen);
                }}
                id="space-between"
                style={{ cursor: "pointer", padding: "8px", gap: "8px" }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Icon icon="pi pi-pencil" size="16px" />
                </div>
                <p>Editar</p>
              </Row>
            )}
            {canEdit && (
              <div className="w-full">
                <Row
                  onClick={() => {
                    setVisibleDeleteTerm(row);
                    setIsPopoverOpen(!isPopoverOpen);
                  }}
                  id="space-between"
                  style={{ cursor: "pointer", padding: "8px", gap: "8px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Icon icon="pi pi-trash" size="16px" />
                  </div>
                  <p>Excluir</p>
                </Row>
              </div>
            )}
          </div>
        }
      >
        <div
          style={{ cursor: "pointer" }}
          onClick={() => setIsPopoverOpen(!isPopoverOpen)}
        >
          {" "}
          <Icon icon="pi pi-ellipsis-v" />
        </div>
      </Popover>
    </Row>
  );
};
export default BeneficiariesEdit;
